// frontend/src/components/LooksProviderConsentModal.js
// Shown the first time a provider (R3/R4/R7) tries to access the Looks feature.
// Provider must agree before they can create looks. Their agreement is persisted
// to the server so the modal is not shown again.
//
// Props:
//   getAuthHeaders  — fn returning { Authorization: 'Bearer ...' } or null
//   getApiBaseUrl   — fn returning base API URL string or null
//   onAgreed        — called after successful server-side consent save
//   onDismiss       — called if provider closes without agreeing (optional)

import React, { useState } from "react";
import axios from "axios";

function LooksProviderConsentModal({ getAuthHeaders, getApiBaseUrl, onAgreed, onDismiss }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleAgree = async () => {
    setSubmitting(true);
    setError("");
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        setError("Unable to verify your session. Please refresh and try again.");
        return;
      }

      await axios.post(
        `${baseURL}/api/looks/provider-consent`,
        { agreed: true },
        { headers },
      );

      if (onAgreed) onAgreed();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to save your agreement. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "32px 28px",
          maxWidth: "520px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "20px" }}>
          <h2 style={{ margin: "0 0 6px 0", fontSize: "20px", fontWeight: "700", color: "#111827" }}>
            📸 Enable Looks
          </h2>
          <p style={{ margin: 0, fontSize: "13px", color: "#6b7280", lineHeight: "1.5" }}>
            Before you can share your work with Looks, please review and accept the terms below.
          </p>
        </div>

        {/* Terms box */}
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            padding: "16px",
            marginBottom: "20px",
            fontSize: "13px",
            color: "#374151",
            lineHeight: "1.6",
          }}
        >
          <p style={{ margin: "0 0 10px 0", fontWeight: "700", color: "#111827" }}>
            Looks Platform Terms
          </p>
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            <li style={{ marginBottom: "8px" }}>
              Looks you create may be displayed anywhere on OGESTYLE — including the shop landing
              page, your professional profile, and the public Looks feed.
            </li>
            <li style={{ marginBottom: "8px" }}>
              This provider consent is a one-time agreement and remains active for future Looks,
              unless your Looks access is barred or suspended due to abuse or policy violations.
            </li>
            <li style={{ marginBottom: "8px" }}>
              You confirm you have permission from your client (customer) to photograph and share
              their image. Consent is requested from customers separately.
            </li>
            <li style={{ marginBottom: "8px" }}>
              OGESTYLE will apply a watermark (logo + your brand name) to all published Looks.
              Original un-watermarked images remain private.
            </li>
            <li style={{ marginBottom: "8px" }}>
              You can archive or remove your Looks at any time from your dashboard.
            </li>
            <li>
              Looks that violate OGESTYLE's content policy may be removed without notice.
            </li>
          </ul>
        </div>

        {error && (
          <p style={{ fontSize: "13px", color: "#dc2626", margin: "0 0 14px 0" }}>{error}</p>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          {onDismiss && (
            <button
              onClick={onDismiss}
              disabled={submitting}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                background: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Not now
            </button>
          )}
          <button
            onClick={handleAgree}
            disabled={submitting}
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              background: submitting ? "#d1d5db" : "#15803d",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: submitting ? "not-allowed" : "pointer",
              fontWeight: "700",
            }}
          >
            {submitting ? "Saving..." : "I Agree — Enable Looks"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LooksProviderConsentModal;
